import { css, html, LitElement, nothing } from 'lit'
import { property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import type { IconName } from '@/components/Icon/types'
import type { LinkTarget } from '@/components/IconButton/types'
import { safeCustomElement } from '@/utilities/custom-element'
import type { ButtonSize, ButtonType, ButtonVariant } from './types'

@safeCustomElement('mdc-button')
export class MdcButton extends LitElement {
  static formAssociated = true

  @property({ type: String }) variant: ButtonVariant = 'primary'
  @property({ type: String }) size: ButtonSize = 'md'
  @property({ type: String, attribute: 'left-icon' }) leftIcon: IconName | '' = ''
  @property({ type: String, attribute: 'right-icon' }) rightIcon: IconName | '' = ''
  @property({ type: String }) href = ''
  @property({ type: String }) target: LinkTarget = '_self'
  @property({ type: String }) rel = ''
  @property({ type: Boolean }) disabled = false
  @property({ type: String }) type: ButtonType = 'button'
  @property({ type: String }) name = ''
  @property({ type: String }) value = ''

  private _internals!: ElementInternals

  constructor() {
    super()
    this._internals = this.attachInternals()
  }

  static styles = css`
		:host {
			display: inline-flex;
			font-family: var(--typography-font-family-sans);
			/* CSS Custom Properties for customization */
			--mdc-button-min-width: 4rem;
			--mdc-button-font-family: var(--typography-font-family-sans);
			--mdc-button-font-size: var(--text-062-and-half, 0.625rem);
			--mdc-button-font-size-lg: var(--text-068-and-three-quatars, 0.6875rem);
			--mdc-button-font-weight: 450;
			--mdc-button-line-height: 1;
			--mdc-button-letter-spacing: 0.04125rem;
			--mdc-button-text-transform: uppercase;
			--mdc-button-border-radius: var(--radius-md, 0.5rem);
			--mdc-button-transition: background-color 0.3s, border-color 0.3s, color 0.3s;
			--mdc-button-gap: var(--spacing-050, 0.5rem);
			--mdc-button-focus-ring: 0 0 0 2px var(--color-functional-blue-300);
			--mdc-button-padding-sm: var(--spacing-050, 0.5rem) var(--spacing-075, 0.75rem);
			--mdc-button-height-sm: 2.25rem;
			--mdc-button-padding-md: var(--form-field-padding-vertical, 0.5rem) var(--spacing-100, 1rem);
			--mdc-button-height-md: var(--form-field-height, 2.75rem);
			--mdc-button-padding-lg: var(--spacing-075, 0.75rem) var(--spacing-150, 1.5rem);
			--mdc-button-height-lg: 3.25rem;
			--mdc-button-icon-size: var(--form-field-icon-size, 1.125rem);

			--mdc-button-primary-color: var(--color-functional-gray-white);
			--mdc-button-primary-bg: var(--color-functional-gray-black);
			--mdc-button-primary-bg-hover: var(--color-functional-gray-800);
			--mdc-button-primary-disabled-color: var(--color-functional-gray-300);
			--mdc-button-primary-disabled-bg: var(--color-functional-gray-50);

			--mdc-button-secondary-color: var(--color-functional-gray-black);
			--mdc-button-secondary-border: 1px solid var(--color-functional-gray-75);
			--mdc-button-secondary-border-hover: var(--color-functional-gray-200);
			--mdc-button-secondary-disabled-color: var(--color-functional-gray-300);
			--mdc-button-secondary-disabled-border: var(--color-functional-gray-75);

			--mdc-button-tertiary-color: var(--color-functional-gray-black);
			--mdc-button-tertiary-bg: var(--color-functional-gray-white);
			--mdc-button-tertiary-bg-hover: var(--color-functional-gray-25);
			--mdc-button-tertiary-text-decoration: underline;
			--mdc-button-tertiary-disabled-color: var(--color-functional-gray-300);

			--mdc-button-inverse-primary-color: var(--color-functional-gray-black);
			--mdc-button-inverse-primary-bg: var(--color-functional-gray-white);
			--mdc-button-inverse-primary-bg-hover: var(--color-functional-gray-25);
			--mdc-button-inverse-primary-disabled-color: var(--color-functional-gray-300);
			--mdc-button-inverse-primary-disabled-bg: var(--color-functional-gray-50);

			--mdc-button-inverse-secondary-color: var(--color-functional-gray-white);
			--mdc-button-inverse-secondary-border: 1px solid var(--color-functional-gray-400);
			--mdc-button-inverse-secondary-border-hover: var(--color-functional-gray-200);
			--mdc-button-inverse-secondary-disabled-color: var(--color-functional-gray-300);
			--mdc-button-inverse-secondary-disabled-border: var(--color-functional-gray-400);

			--mdc-button-inverse-tertiary-color: var(--color-functional-gray-white);
			--mdc-button-inverse-tertiary-bg-hover: rgba(255, 255, 255, 0.1);
			--mdc-button-inverse-tertiary-text-decoration: underline;
			--mdc-button-inverse-tertiary-disabled-color: var(--color-functional-gray-300);

			--mdc-button-pro-starter-color: var(--color-functional-gray-black);
			--mdc-button-pro-starter-bg: var(--color-pro-pink-300);
			--mdc-button-pro-starter-bg-hover: var(--color-pro-pink-350);
			--mdc-button-pro-starter-disabled-color: var(--color-functional-gray-300);
			--mdc-button-pro-starter-disabled-bg: var(--color-functional-gray-50);

			--mdc-button-pro-pro-color: var(--color-functional-gray-black);
			--mdc-button-pro-pro-bg: var(--color-pro-orange-300);
			--mdc-button-pro-pro-bg-hover: var(--color-pro-orange-350);
			--mdc-button-pro-pro-disabled-color: var(--color-functional-gray-300);
			--mdc-button-pro-pro-disabled-bg: var(--color-functional-gray-50);

			--mdc-button-pro-team-color: var(--color-functional-gray-black);
			--mdc-button-pro-team-bg: var(--color-pro-tomato-400);
			--mdc-button-pro-team-bg-hover: var(--color-pro-tomato-450);
			--mdc-button-pro-team-disabled-color: var(--color-functional-gray-300);
			--mdc-button-pro-team-disabled-bg: var(--color-functional-gray-50);
		}

		.mdc-button {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			min-width: var(--mdc-button-min-width);
			cursor: pointer;
			font-family: var(--mdc-button-font-family);
			font-size: var(--mdc-button-font-size);
			font-weight: var(--mdc-button-font-weight);
			line-height: var(--mdc-button-line-height);
			letter-spacing: var(--mdc-button-letter-spacing);
			text-align: center;
			text-transform: var(--mdc-button-text-transform);
			border: none;
			border-radius: var(--mdc-button-border-radius);
			background: none;
			transition: var(--mdc-button-transition);
			text-decoration: none;
			margin: 0 !important;
			white-space: nowrap;
			gap: var(--mdc-button-gap);
			box-sizing: border-box;
		}

		/* breakpoints.sm */
		@media (min-width: 640px) {
			.mdc-button {
				font-size: var(--mdc-button-font-size-lg);
			}
		}

		.mdc-button:focus-visible {
			box-shadow: var(--mdc-button-focus-ring);
			outline: none;
		}

		.mdc-button:disabled,
		.mdc-button[aria-disabled='true'] {
			cursor: not-allowed;
			pointer-events: none;
		}

		.mdc-button--size-md {
			padding: var(--mdc-button-padding-md);
			height: var(--mdc-button-height-md);
			box-sizing: border-box;
		}

		.mdc-button--size-lg {
			padding: var(--mdc-button-padding-lg);
			height: var(--mdc-button-height-lg);
			box-sizing: border-box;
		}

		.mdc-button--size-sm {
			padding: var(--mdc-button-padding-sm);
			height: var(--mdc-button-height-sm);
			box-sizing: border-box;
		}

		.mdc-button__icon {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: var(--mdc-button-icon-size);
			height: var(--mdc-button-icon-size);
			pointer-events: none;
		}

		.mdc-button--primary {
			color: var(--mdc-button-primary-color);
			background-color: var(--mdc-button-primary-bg);
		}

		.mdc-button--primary:hover,
		.mdc-button--primary:focus-visible {
			background-color: var(--mdc-button-primary-bg-hover);
		}

		.mdc-button--primary:disabled,
		.mdc-button--primary[aria-disabled='true'] {
			color: var(--mdc-button-primary-disabled-color);
			background-color: var(--mdc-button-primary-disabled-bg);
		}

		.mdc-button--secondary {
			color: var(--mdc-button-secondary-color);
			border: var(--mdc-button-secondary-border);
		}

		.mdc-button--secondary:hover {
			border-color: var(--mdc-button-secondary-border-hover);
		}

		.mdc-button--secondary:focus-visible {
			border-color: var(--mdc-button-secondary-border-hover);
		}

		.mdc-button--secondary:disabled,
		.mdc-button--secondary[aria-disabled='true'] {
			color: var(--mdc-button-secondary-disabled-color);
			border-color: var(--mdc-button-secondary-disabled-border);
		}

		.mdc-button--tertiary {
			color: var(--mdc-button-tertiary-color);
			background-color: var(--mdc-button-tertiary-bg);
			text-decoration: var(--mdc-button-tertiary-text-decoration);
		}

		.mdc-button--tertiary:hover,
		.mdc-button--tertiary:focus-visible {
			background-color: var(--mdc-button-tertiary-bg-hover);
		}

		.mdc-button--tertiary:disabled,
		.mdc-button--tertiary[aria-disabled='true'] {
			color: var(--mdc-button-tertiary-disabled-color);
		}

		.mdc-button--inverse-primary {
			color: var(--mdc-button-inverse-primary-color);
			background-color: var(--mdc-button-inverse-primary-bg);
		}

		.mdc-button--inverse-primary:hover,
		.mdc-button--inverse-primary:focus-visible {
			background-color: var(--mdc-button-inverse-primary-bg-hover);
		}

		.mdc-button--inverse-primary:disabled,
		.mdc-button--inverse-primary[aria-disabled='true'] {
			color: var(--mdc-button-inverse-primary-disabled-color);
			background-color: var(--mdc-button-inverse-primary-disabled-bg);
		}

		.mdc-button--inverse-secondary {
			color: var(--mdc-button-inverse-secondary-color);
			border: var(--mdc-button-inverse-secondary-border);
		}

		.mdc-button--inverse-secondary:hover {
			border-color: var(--mdc-button-inverse-secondary-border-hover);
		}

		.mdc-button--inverse-secondary:focus-visible {
			border-color: var(--mdc-button-inverse-secondary-border-hover);
		}

		.mdc-button--inverse-secondary:disabled,
		.mdc-button--inverse-secondary[aria-disabled='true'] {
			color: var(--mdc-button-inverse-secondary-disabled-color);
			border-color: var(--mdc-button-inverse-secondary-disabled-border);
		}

		.mdc-button--inverse-tertiary {
			color: var(--mdc-button-inverse-tertiary-color);
			text-decoration: var(--mdc-button-inverse-tertiary-text-decoration);
		}

		.mdc-button--inverse-tertiary:hover,
		.mdc-button--inverse-tertiary:focus-visible {
			background-color: var(--mdc-button-inverse-tertiary-bg-hover);
		}

		.mdc-button--inverse-tertiary:disabled,
		.mdc-button--inverse-tertiary[aria-disabled='true'] {
			color: var(--mdc-button-inverse-tertiary-disabled-color);
		}

		.mdc-button--pro-starter {
			color: var(--mdc-button-pro-starter-color);
			background-color: var(--mdc-button-pro-starter-bg);
		}

		.mdc-button--pro-starter:hover,
		.mdc-button--pro-starter:focus-visible {
			background-color: var(--mdc-button-pro-starter-bg-hover);
		}

		.mdc-button--pro-starter:disabled,
		.mdc-button--pro-starter[aria-disabled='true'] {
			color: var(--mdc-button-pro-starter-disabled-color);
			background-color: var(--mdc-button-pro-starter-disabled-bg);
		}

		.mdc-button--pro-pro {
			color: var(--mdc-button-pro-pro-color);
			background-color: var(--mdc-button-pro-pro-bg);
		}

		.mdc-button--pro-pro:hover,
		.mdc-button--pro-pro:focus-visible {
			background-color: var(--mdc-button-pro-pro-bg-hover);
		}

		.mdc-button--pro-pro:disabled,
		.mdc-button--pro-pro[aria-disabled='true'] {
			color: var(--mdc-button-pro-pro-disabled-color);
			background-color: var(--mdc-button-pro-pro-disabled-bg);
		}

		.mdc-button--pro-team {
			color: var(--mdc-button-pro-team-color);
			background-color: var(--mdc-button-pro-team-bg);
		}

		.mdc-button--pro-team:hover,
		.mdc-button--pro-team:focus-visible {
			background-color: var(--mdc-button-pro-team-bg-hover);
		}

		.mdc-button--pro-team:disabled,
		.mdc-button--pro-team[aria-disabled='true'] {
			color: var(--mdc-button-pro-team-disabled-color);
			background-color: var(--mdc-button-pro-team-disabled-bg);
		}
	`

  private handleClick(e: Event) {
    if (this.href) return

    if (this.disabled) {
      e.preventDefault()
      e.stopPropagation()
      return
    }

    const form = this._internals.form
    if (!form) return

    if (this.type === 'submit') {
      // Inject a transient hidden input so the server sees the button's name/value
      if (this.name) {
        const hidden = document.createElement('input')
        hidden.type = 'hidden'
        hidden.name = this.name
        hidden.value = this.value
        form.appendChild(hidden)
        // Clean up as a safety net (page usually navigates on submit)
        requestAnimationFrame(() => hidden.remove())
      }
      form.requestSubmit()
    } else if (this.type === 'reset') {
      form.reset()
    }
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled
  }

  render() {
    const className = `mdc-button mdc-button--${this.variant} mdc-button--size-${this.size}`

    const content = html`
			${
        this.leftIcon
          ? html`<span class="mdc-button__icon mdc-button__icon--left" aria-hidden="true"><mdc-icon name=${this.leftIcon}></mdc-icon></span>`
          : nothing
      }
			<span class="mdc-button__label"><slot></slot></span>
			${
        this.rightIcon
          ? html`<span class="mdc-button__icon mdc-button__icon--right" aria-hidden="true"><mdc-icon name=${this.rightIcon}></mdc-icon></span>`
          : nothing
      }
		`

    if (this.href) {
      return html`
				<a
					class=${className}
					href=${this.href}
					target=${ifDefined(this.target === '_self' ? undefined : this.target)}
					rel=${ifDefined(this.rel || undefined)}
					aria-disabled=${ifDefined(this.disabled ? 'true' : undefined)}
				>
					${content}
				</a>
			`
    }

    return html`
			<button type="button" class=${className} ?disabled=${this.disabled} aria-disabled=${ifDefined(this.disabled ? 'true' : undefined)} @click=${this.handleClick}>
				${content}
			</button>
		`
  }
}
